import { formDataToObject, postData } from '../utils/ajax-utils';
import { formatPrice } from '../utils/utils';
import { ModalHTMLElement } from './modal';
import { NumberModifier } from './number-modifier';

export function initAddToCart() : void {
    document.querySelectorAll<ModalHTMLElement>('.js-modal-add-to-cart').forEach($modal => {
        new AddToCartModal($modal);
    });

    // Create a new AddToCartModal instance each time a "modal_ajax_content_loaded" event is sent with the action "add_to_cart" in the detail of the event
    document.addEventListener('modal_ajax_content_loaded', e => {
        if ('add_to_cart' === e.detail.action) {
            new AddToCartModal(e.detail.$modal);
        }
    });
}

type ConfigurationType = {
    service?: HTMLInputElement,
    declination?: HTMLInputElement|null,
    optionalProduct?: HTMLInputElement|null,
    faceToFace?: HTMLInputElement|null,
    domains?: HTMLInputElement[],
};

class AddToCartModal {
    configuration: ConfigurationType = {};
    totalPrice: number = 0;
    $modal: ModalHTMLElement;
    $successModal: ModalHTMLElement;
    $counterItemCartMenu: HTMLElement;
    $form: HTMLFormElement|null = null;
    $totalContainer: HTMLElement|null = null;
    $service: HTMLInputElement|null = null;
    $quantity: HTMLSelectElement|null = null;
    $declinations: NodeListOf<HTMLInputElement>|null = null;
    $optionalProducts: NodeListOf<HTMLInputElement>|null = null;
    $faceToFaces: NodeListOf<HTMLInputElement>|null = null;
    $wildcardOption: HTMLInputElement|null = null;
    $domainsFields: NodeListOf<HTMLInputElement>|null = null;

    constructor($modal: ModalHTMLElement) {
        this.$modal = $modal;
        this.$successModal = document.querySelector(`#${this.$modal.dataset.prefix ?? ''}add-to-cart-success`) as ModalHTMLElement;
        this.$counterItemCartMenu = document.querySelector('.js-cart-product-number') as HTMLElement;

        this.$form = $modal.querySelector<HTMLFormElement>('form');

        if (!this.$form) {
            return;
        }

        this.$totalContainer = this.$form.querySelector('.js-total') as HTMLElement;
        this.totalPrice = parseFloat(this.$totalContainer.innerText.replace(',', '.').replace(' ', ''));
        this.$service = this.$form.querySelector<HTMLInputElement>('.js-service');

        this.configuration = {};

        if (this.$service) {
            this.configuration.service = this.$service;
        }

        this.$quantity = this.$form.querySelector<HTMLSelectElement>('.js-quantity');
        this.$declinations = this.$form.querySelectorAll<HTMLInputElement>('.js-declination');
        this.$optionalProducts = this.$form.querySelectorAll<HTMLInputElement>('.js-optional-product');
        this.$faceToFaces = this.$form.querySelectorAll<HTMLInputElement>('.js-face-to-face');
        this.$wildcardOption = this.$form.querySelector<HTMLInputElement>('.js-wildcard-option');
        this.$domainsFields = this.$form.querySelectorAll<HTMLInputElement>('.js-domain, .js-subdomain');

        this.addFormEvents();

        const $numberModifiers = this.$modal.querySelectorAll<HTMLElement>('.js-number-modifier:not(.initialized)');
        if ($numberModifiers.length) {
            $numberModifiers.forEach($numberModifier => {
                new NumberModifier($numberModifier);
            });
        }
    }

    /**
     * Add the form events
     * 
     * @return {void}
     */
    addFormEvents() : void {
        // On form submit
        this.$form?.addEventListener('submit', (e) => this.onSubmitEvent(e));

        // On declination change
        if (this.$declinations?.length) {
            this.$declinations.forEach($declination => {
                $declination.addEventListener('change', () => this.onDeclinationChangeEvent());
            });
            this.configuration.declination = this.getSelectedDeclination();
        }

        // On optional product change
        if (this.$optionalProducts?.length) {
            this.$optionalProducts.forEach($optionalProduct => {
                $optionalProduct.addEventListener('click', (e) => this.onOptionalProductClickEvent(e));
            });
            this.configuration.optionalProduct = this.getSelectedOptionalProduct();
        }

        // On face to face change
        if (this.$faceToFaces?.length) {
            this.$faceToFaces.forEach($faceToFace => {
                $faceToFace.addEventListener('change', () => this.onFaceToFaceChangeEvent());
            });
            this.configuration.faceToFace = this.getSelectedFaceToFace();
        }

        // On wildcard option change
        if (this.$wildcardOption) {
            this.$wildcardOption.addEventListener('change', () => this.onWildcardOptionChangeEvent());
        }

        // On subDomain change
        this.$domainsFields?.forEach($field => {
            if ($field.classList.contains('js-subdomain')) {
                $field.addEventListener('change', () => this.onDomainsChange());
            }
        });

        // When adding a new domain
        document.addEventListener('collection_field_item_added', evt => {
            if (!this.$form) {
                return;
            }

            // Reset the registered domains and subDomains
            this.$domainsFields = this.$form.querySelectorAll('.js-domain, .js-subdomain');

            // Bind the on change event on new subDomain field
            const $subDomain = evt.detail.$item.querySelector('.js-subdomain');
            if ($subDomain) {
                $subDomain.addEventListener('change', () => this.onDomainsChange());
            }

            // Init number modifier on new Elements
            const $numberModifiers = evt.detail.$item.querySelectorAll<HTMLElement>('.js-number-modifier:not(.initialized)');
            if ($numberModifiers.length) {
                $numberModifiers.forEach($numberModifier => {
                    new NumberModifier($numberModifier);
                });
            }

            this.onDomainsChange();
        });

        // When removing a domain
        document.addEventListener('collection_field_item_after_deleted', () => {
            if (!this.$form) {
                return;
            }

            // Reset the registered domains and subDomains
            this.$domainsFields = this.$form.querySelectorAll('.js-domain, .js-subdomain');

            this.onDomainsChange();
        });
        this.onDomainsChange();

        // On quantity change
        this.$quantity?.addEventListener('change', () => {
            this.setTotalPrice();
        });

        this.setTotalPrice();
    }

    /**
     * Get the selected declination
     * 
     * @return {HTMLInputElement|null} The selected declination Element
     */
    getSelectedDeclination() : HTMLInputElement|null {
        let $selected = null;

        this.$declinations?.forEach($declination => {
            if ($declination.checked) {
                $selected = $declination;
            }
        });

        return $selected;
    }

    /**
     * On declination change event
     * 
     * @return {void}
     */
    onDeclinationChangeEvent() : void {
        this.configuration.declination = this.getSelectedDeclination();

        this.setTotalPrice();
    }

    /**
     * Get the selected optional product
     * 
     * @return {HTMLInputElement|null} The selected optional product Element
     */
    getSelectedOptionalProduct() : HTMLInputElement|null {
        let $selected = null;

        this.$optionalProducts?.forEach($optionalProduct => {
            if ($optionalProduct.checked) {
                $selected = $optionalProduct;
            }
        });

        return $selected;
    }

    /**
     * On optional product click event
     * 
     * @param {MouseEvent} evt
     * 
     * @return {void}
     */
    onOptionalProductClickEvent(evt: MouseEvent) : void {
        const $target = evt.target as HTMLInputElement;

        if ($target.classList.contains('checked')) {
            $target.classList.remove('checked');
            $target.checked = false;
        } else {
            $target.classList.add('checked');
        }
        
        this.configuration.optionalProduct = this.getSelectedOptionalProduct();

        this.setTotalPrice();
    }

    /**
     * Get the selected face to face
     * 
     * @return {HTMLInputElement|null} The selected face to face Element
     */
    getSelectedFaceToFace() : HTMLInputElement|null {
        let $selected = null;

        this.$faceToFaces?.forEach($faceToFace => {
            if ($faceToFace.checked) {
                $selected = $faceToFace;
            }
        });

        return $selected;
    }

    /**
     * On face to face change event
     * 
     * @return {void}
     */
    onFaceToFaceChangeEvent() : void {
        this.configuration.faceToFace = this.getSelectedFaceToFace();

        this.setTotalPrice();
    }

    /**
     * On wildcard option change event
     * 
     * @return {void}
     */
    onWildcardOptionChangeEvent() : void {
        if (!this.$form) {
            return;
        }

        // Loading
        const $submitButton = this.$form.querySelector<HTMLButtonElement>('.js-submit-button');

        if ($submitButton) {
            $submitButton.setAttribute('disabled', 'disabled');
            $submitButton.querySelector('.loading')?.classList.remove('hidden');
        }

        const args = {
            action: 'add_to_cart',
            service_id: this.$wildcardOption?.dataset.service ?? '',
        };

        postData(scriptVar.ajaxUrl, args).then(response => {
            // Change the content of the modal
            const $modalContent = this.$modal.querySelector('.js-modal-content') as HTMLElement;
            $modalContent.innerHTML = response.content;

            // Create new instance of AddToCartModal with new content
            new AddToCartModal(this.$modal);
        }).catch(error => {
            console.error('Error:', error);
        });
    }

    /**
     * Register the domains into configuration
     * 
     * @return {void}
     */
    onDomainsChange() : void {
        this.configuration.domains = Array.from(this.$domainsFields || []);

        this.setTotalPrice();
    }

    /**
     * Calculate the total price and display it into the $totalContainer
     * 
     * @return {void}
     */
    setTotalPrice() : void {
        let totalPrice = 0;

        const configurationKeys = Object.keys(this.configuration) as (keyof ConfigurationType)[];
        configurationKeys.forEach(key => {
            const $configurationEl = this.configuration[key];

            if (Array.isArray($configurationEl)) {
                $configurationEl.forEach($configEl => {
                    if ($configEl.classList.contains('js-subdomain')) {
                        totalPrice += parseFloat($configEl.dataset.price ?? '0') * parseFloat($configEl.value ?? '0');
                    } else {
                        totalPrice += parseFloat($configEl.dataset.price ?? '0');
                    }
                });
            } else {
                totalPrice += parseFloat($configurationEl?.dataset.price ?? '0');
            }
        });

        totalPrice = totalPrice * parseInt(this.$quantity?.value ?? '0');

        if (this.$totalContainer) {
            this.$totalContainer.innerText = formatPrice(totalPrice);
        }
    }

    /**
     * Increment the value in the cart item menu
     * 
     * @return {void}
     */
    incrementCartMenuQuantity() : void {
        if (this.$counterItemCartMenu.innerHTML === '') {
            this.$counterItemCartMenu.innerHTML = '1';
        } else {
            this.$counterItemCartMenu.innerHTML = (parseInt(this.$counterItemCartMenu.innerHTML, 10) + 1).toString();
        }
    }

    /**
     * On form submit event
     * 
     * @param {SubmitEvent} evt
     * 
     * @return {void}
     */
    onSubmitEvent(evt: SubmitEvent) : void {
        evt.preventDefault();
        evt.stopPropagation();

        if (!this.$form) {
            return;
        }

        // Loading
        const $submitButton = this.$form.querySelector<HTMLButtonElement>('.js-submit-button');
        $submitButton?.setAttribute('disabled', 'disabled');
        $submitButton?.querySelector('.loading')?.classList.remove('hidden');

        const args = {
            action: 'add_to_cart',
            service_id: $submitButton?.dataset.service ?? '',
            ...formDataToObject(new FormData(this.$form)),
        };

        postData(scriptVar.ajaxUrl, args).then(response => {
            // Change the content of the modal
            const $modalContent = this.$modal.querySelector('.js-modal-content') as HTMLElement;
            $modalContent.innerHTML = response.content;

            if (true === response.success) {
                // Close the add-to-cart modal
                const closeModalEvent = new CustomEvent('close_modal', {
                    detail: {
                        $modal: this.$modal,
                    },
                });
                document.dispatchEvent(closeModalEvent);

                const $crossSelling = this.$successModal.querySelector<ModalHTMLElement>('.js-cross-selling');
                if ($crossSelling) {
                    $crossSelling.innerHTML = response.cross_selling_html;
                }

                // Open the success modal
                const openModalEvent = new CustomEvent('open_modal', {
                    detail: {
                        $modal: this.$successModal,
                    },
                });
                document.dispatchEvent(openModalEvent);

                // Dispatch an event with the new cart item detail
                document.dispatchEvent(new CustomEvent('add_to_cart', {
                    detail: {
                        cart: {
                            total: response.cart.total,
                            products: response.cart.services,
                        },
                        product: {
                            name: response.service.name,
                            price: response.service.price,
                            type: response.service.type,
                            duration: response.service.duration,
                            face_to_face: response.service.face_to_face,
                            optional_product: response.service.optional_product,
                            quantity: response.service.quantity,
                        },
                    },
                }));

                this.incrementCartMenuQuantity();
            } else {
                $modalContent.scrollTo(0, 0);
            }

            // Create new instance of AddToCartModal with new content
            new AddToCartModal(this.$modal);
        }).catch(error => {
            console.error('Error:', error);
        });
    }
}
